import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Post, PostFormValues } from '../../types'
import { adminApi } from '../../api/adminApi'
import { toast } from '../../store/adminStore'
import { slugify, calcReadingTime, stripHtml, toDatetimeLocal } from '../../utils/htmlUtils'
import { Button }       from '../ui/Button'
import { Input }        from '../ui/Input'
import { Textarea }     from '../ui/Textarea'
import { Select }       from '../ui/Select'
import { WysiwygEditor }  from './WysiwygEditor'
import { HtmlEditor }    from './HtmlEditor'
import { TagInput }      from './TagInput'
import { FileUploadEditor, type ExtractedPostMeta } from './FileUploadEditor'

interface PostFormProps {
  initial?: Post
}

const STATUS_OPTIONS = [
  { value: 'draft',     label: 'Draft' },
  { value: 'published', label: 'Published' },
]

const CATEGORY_OPTIONS = [
  { value: 'frontend',              label: 'Frontend' },
  { value: 'backend',               label: 'Backend' },
  { value: 'devops',                label: 'DevOps' },
  { value: 'databases',             label: 'Databases' },
  { value: 'algorithms',            label: 'Algorithms' },
  { value: 'tools',                 label: 'Tools' },
  { value: 'career',                label: 'Career' },
  { value: 'general',               label: 'General' },
  { value: 'architecture-patterns', label: 'Architecture & Patterns' },
  { value: 'cloud-architecture',    label: 'Cloud Architecture' },
  { value: 'system-design',         label: 'System Design' },
  { value: 'azure-cloud',           label: 'Azure & Cloud' },
  { value: 'csharp-dotnet',         label: 'C# / .NET' },
  { value: 'artificial-intelligence', label: 'Artificial Intelligence' },
]

const EMPTY: PostFormValues = {
  id:             '',
  slug:           '',
  title:          '',
  summary:        '',
  bodyHtml:       '',
  bodyText:       '',
  author:         'CodeToByte',
  publishedAt:    new Date().toISOString(),
  updatedAt:      undefined,
  tags:           [],
  category:       'general',
  readingTimeMin: 1,
  coverImageUrl:  '',
  status:         'draft',
  featured:       false,
}

export function PostForm({ initial }: PostFormProps) {
  const navigate = useNavigate()
  const isEdit = !!initial

  const [form, setForm] = useState<PostFormValues>(() =>
    initial
      ? { ...initial, updatedAt: initial.updatedAt ?? undefined }
      : EMPTY
  )
  const [bodyTab, setBodyTab] = useState<'wysiwyg' | 'html' | 'upload'>(
    () => initial?.rawHtmlFile ? 'upload' : 'wysiwyg'
  )
  const [slugManual, setSlugManual] = useState(isEdit)
  const [saving, setSaving] = useState(false)

  // Auto-slug from title while user hasn't manually edited slug
  useEffect(() => {
    if (!slugManual && form.title) {
      setForm((f) => ({ ...f, slug: slugify(f.title) }))
    }
  }, [form.title, slugManual])

  // Auto reading-time from body (skipped for uploaded HTML — value comes from post:minread meta)
  useEffect(() => {
    if (form.rawHtmlFile) return
    const text = stripHtml(form.bodyHtml)
    setForm((f) => ({ ...f, bodyText: text, readingTimeMin: calcReadingTime(text) }))
  }, [form.bodyHtml, form.rawHtmlFile])

  const set = <K extends keyof PostFormValues>(key: K, val: PostFormValues[K]) =>
    setForm((f) => ({ ...f, [key]: val }))

  const handleFileLoaded = (extracted: ExtractedPostMeta) => {
    setForm((f) => ({
      ...f,
      rawHtmlFile: extracted.rawHtmlFile,
      // Auto-fill only fields that were found in the meta tags
      ...(extracted.id             && { id:             extracted.id }),
      ...(extracted.title          && { title:           extracted.title }),
      ...(extracted.slug           && { slug:            extracted.slug }),
      ...(extracted.summary        && { summary:         extracted.summary }),
      ...(extracted.author         && { author:          extracted.author }),
      ...(extracted.publishedAt    && { publishedAt:     extracted.publishedAt }),
      ...(extracted.tags           && { tags:            extracted.tags }),
      ...(extracted.category       && { category:        extracted.category }),
      ...(extracted.status         && { status:          extracted.status }),
      ...(extracted.featured !== undefined && { featured: extracted.featured }),
      ...(extracted.readingTimeMin !== undefined && { readingTimeMin: extracted.readingTimeMin }),
    }))
    if (extracted.slug) setSlugManual(true)
  }

  const handleFileClear = () => {
    setForm((f) => ({ ...f, rawHtmlFile: undefined }))
    setBodyTab('wysiwyg')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) { toast.error('Title is required'); return }
    if (!form.slug.trim())  { toast.error('Slug is required');  return }

    setSaving(true)
    try {
      await adminApi.upsertPost(form)
      toast.success(isEdit ? 'Post updated.' : 'Post created.')
      navigate('/posts')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <form className="post-form" onSubmit={handleSubmit} noValidate>

        {/* ── Identity ───────────────────────────────────────── */}
        <section className="pf-section">
          <h3 className="pf-section-title">Identity</h3>
          <div className="pf-grid-2">

            <Input
              label="Title"
              example="Mastering CPU Thread Affinity in C# — A Deep Dive"
              hint="The public post title shown in listings and the browser tab."
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. Mastering CPU Thread Affinity in C#"
              required
            />

            <div>
              <Input
                label="Slug"
                example="mastering-cpu-thread-affinity-csharp"
                hint="URL-safe identifier used in the post route: /posts/{slug}. Auto-generated from title."
                value={form.slug}
                onChange={(e) => { setSlugManual(true); set('slug', e.target.value) }}
                placeholder="e.g. mastering-cpu-thread-affinity-csharp"
                prefix="/"
                required
              />
            </div>
          </div>
        </section>

        {/* ── Summary ───────────────────────────────────────── */}
        <section className="pf-section">
          <h3 className="pf-section-title">Summary</h3>
          <Textarea
            label="Summary / Excerpt"
            example="Explore how thread affinity can improve cache performance and reduce context-switching overhead in latency-critical .NET applications."
            hint="1–3 sentences shown on the post card and in search snippets. Keep it under 200 characters for best display."
            value={form.summary}
            onChange={(e) => set('summary', e.target.value)}
            placeholder="A concise 1–3 sentence description of what this post covers."
            rows={3}
          />
        </section>

        {/* ── Body ──────────────────────────────────────────── */}
        <section className="pf-section">
          <div className="pf-body-header">
            <h3 className="pf-section-title" style={{ margin: 0 }}>Post Body</h3>
            <div className="pf-tab-switcher">
              <button
                type="button"
                className={`pf-tab ${bodyTab === 'wysiwyg' ? 'pf-tab--active' : ''}`}
                onClick={() => setBodyTab('wysiwyg')}
              >WYSIWYG</button>
              <button
                type="button"
                className={`pf-tab ${bodyTab === 'html' ? 'pf-tab--active' : ''}`}
                onClick={() => setBodyTab('html')}
              >Raw HTML</button>
              <button
                type="button"
                className={`pf-tab ${bodyTab === 'upload' ? 'pf-tab--active' : ''} pf-tab--upload`}
                onClick={() => setBodyTab('upload')}
              >⬆ Upload File</button>
            </div>
          </div>
          <p className="field-example" style={{ marginBottom: 8 }}>
            e.g. Use h2 for main sections, pre/code[class="language-csharp"] for code blocks. The h1 title is rendered by the frontend, so start body content with h2.
          </p>

          {bodyTab === 'wysiwyg' && (
            <WysiwygEditor value={form.bodyHtml} onChange={(v) => set('bodyHtml', v)} />
          )}
          {bodyTab === 'html' && (
            <HtmlEditor value={form.bodyHtml} onChange={(v) => set('bodyHtml', v)} />
          )}
          {bodyTab === 'upload' && (
            <FileUploadEditor
              currentFile={form.rawHtmlFile}
              onFileLoaded={handleFileLoaded}
              onClear={handleFileClear}
            />
          )}

          {bodyTab === 'upload' ? (
            <div className="pf-upload-minread">
              <Input
                label="Min Read (minutes)"
                type="number"
                min="1"
                hint='Sourced from the post:minread meta tag in the HTML file. Adjust manually if needed.'
                value={String(form.readingTimeMin)}
                onChange={(e) => set('readingTimeMin', Math.max(1, parseInt(e.target.value, 10) || 1))}
                style={{ maxWidth: 160 }}
              />
            </div>
          ) : (
            <div className="pf-reading-time">
              <span>Estimated reading time:</span>
              <strong>{form.readingTimeMin} min</strong>
              <span className="pf-reading-time-note">
                (auto-calculated · {stripHtml(form.bodyHtml).trim().split(/\s+/).filter(Boolean).length} words)
              </span>
            </div>
          )}
        </section>

        {/* ── Classification ────────────────────────────────── */}
        <section className="pf-section">
          <h3 className="pf-section-title">Classification</h3>
          <div className="pf-grid-2">

            <Select
              label="Category"
              example="backend"
              hint="Broad topic grouping used for filtering in the blog."
              options={CATEGORY_OPTIONS}
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
            />

            <TagInput
              label="Tags"
              example="typescript, dotnet, performance, concurrency"
              hint="Press Enter or comma after each tag. Tags help readers discover related posts."
              tags={form.tags}
              onChange={(tags) => set('tags', tags)}
            />
          </div>
        </section>

        {/* ── Publishing ────────────────────────────────────── */}
        <section className="pf-section">
          <h3 className="pf-section-title">Publishing</h3>
          <div className="pf-grid-3">

            <Select
              label="Status"
              example="published"
              hint="Draft posts are not visible to public readers."
              options={STATUS_OPTIONS}
              value={form.status}
              onChange={(e) => set('status', e.target.value as PostFormValues['status'])}
            />

            <Input
              label="Published At"
              example="2026-04-12T10:00"
              hint="When this post should appear as published. Shown in post meta."
              type="datetime-local"
              value={toDatetimeLocal(form.publishedAt)}
              onChange={(e) => set('publishedAt', new Date(e.target.value).toISOString())}
            />

            <Input
              label="Author"
              example="CodeToByte"
              hint="Display name shown in the post meta."
              value={form.author}
              onChange={(e) => set('author', e.target.value)}
              placeholder="e.g. CodeToByte"
            />
          </div>

          <div className="pf-grid-2" style={{ marginTop: 16 }}>

            <Input
              label="Cover Image URL"
              example="https://cdn.codetobyte.com/images/cpu-threads.jpg"
              hint="Optional. Full HTTPS URL to a banner image shown at the top of the post."
              value={form.coverImageUrl ?? ''}
              onChange={(e) => set('coverImageUrl', e.target.value || undefined)}
              placeholder="https://…"
              type="url"
            />

            <div className="pf-checkbox-field">
              <label className="field-label">Featured</label>
              <p className="field-example">e.g. Check to pin this post to the homepage hero section.</p>
              <label className="pf-checkbox-label">
                <input
                  type="checkbox"
                  className="pf-checkbox"
                  checked={form.featured}
                  onChange={(e) => set('featured', e.target.checked)}
                />
                <span>Show this post as a featured / highlighted post</span>
              </label>
            </div>
          </div>
        </section>

        {/* ── Actions ───────────────────────────────────────── */}
        <div className="pf-actions">
          <Button type="button" variant="ghost" onClick={() => navigate('/posts')}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="secondary"
            loading={saving}
            onClick={() => set('status', 'draft')}
          >
            Save as Draft
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={saving}
            onClick={() => set('status', 'published')}
          >
            {isEdit ? 'Update Post' : 'Publish Post'}
          </Button>
        </div>
      </form>

      <style>{`
        .post-form { display: flex; flex-direction: column; gap: 0; }
        .pf-section {
          padding: 24px 0;
          border-bottom: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .pf-section:last-of-type { border-bottom: none; }
        .pf-section-title {
          font-size: 0.8rem;
          font-weight: 700;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .pf-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .pf-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
        @media (max-width: 700px) {
          .pf-grid-2, .pf-grid-3 { grid-template-columns: 1fr; }
        }
        .pf-body-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
        }
        .pf-tab-switcher {
          display: flex;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          overflow: hidden;
        }
        .pf-tab {
          padding: 6px 14px;
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--text-muted);
          transition: background 0.12s, color 0.12s;
        }
        .pf-tab--active { background: var(--accent); color: #fff; }
        .pf-tab:not(.pf-tab--active):hover { background: var(--bg-hover); color: var(--text-primary); }
        .pf-tab--upload:not(.pf-tab--active) { color: var(--color-info); }
        .pf-tab--upload.pf-tab--active { background: var(--color-info); }
        .pf-upload-minread { margin-top: 8px; }
        .pf-reading-time {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.78rem;
          color: var(--text-muted);
          margin-top: 8px;
        }
        .pf-reading-time strong { color: var(--text-secondary); }
        .pf-reading-time-note { color: var(--text-muted); font-style: italic; }
        .pf-checkbox-field { display: flex; flex-direction: column; gap: 5px; }
        .pf-checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 0.875rem;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 9px 12px;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          background: var(--bg-input);
          transition: border-color 0.15s;
        }
        .pf-checkbox-label:hover { border-color: var(--accent); }
        .pf-checkbox {
          width: 16px; height: 16px;
          accent-color: var(--accent);
          flex-shrink: 0;
          cursor: pointer;
        }
        .pf-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding-top: 24px;
          border-top: 1px solid var(--border);
          flex-wrap: wrap;
        }
      `}</style>
    </>
  )
}
